import { Button, ButtonGroup, Divider, Permission } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { ReportingProfileContainer } from '../../../containers/reporting/ReportingProfileContainer';
import ContentConstants from '../../../contentConstants';
import {
	AudienceSourceType,
	IBenchmark,
	IBenchmarksActionCreators,
	IBreakoutsActionCreators,
	IReportingSetupActionCreators,
	ISurveyBenchmarkActionCreators,
	ISurveyBuilderActionCreators,
	UserType,
} from '../../../models';
import { IBreakoutModelVm } from '../../../models/IBreakoutModel';
import PermissionConstants from '../../../permissionConstants';
import { IBenchmarksState } from '../../../store/BenchmarksStore';
import { IBreakoutState } from '../../../store/BreakoutsStore';
import { IReportingSetupState } from '../../../store/ReportingSetupStore';
import { ISurveyBuilderState } from '../../../store/SurveyBuilderStore';
import { ValidationCollectionHelper } from '../../../utilities';
import { DistributionHelper } from '../../../utilities/DistributionHelper';
import { IUserFeatureAccessHelper } from '../../../utilities/FeatureFlags';
import { CardLayout, SaveButtonsContainer } from '../../common';
import { SurveyNoteHeaderContainer } from '../../notes/SurveyNoteHeaderContainer';
import { ParticipationMonitorBreakouts } from '../../participation-monitor/ParticipationMonitorBreakouts';
import { ReportingBenchmarks } from './ReportingBenchmarks';
import { ReportingTrends } from './ReportingTrends';
import { ReportingUsers } from './ReportingUsers';
import { SurveyResponseStreamSetup } from './SurveyResponseStreamSetup';

export type IReportingSetupProps = ISurveyBuilderState &
	IBenchmarksState &
	IBreakoutState &
	IReportingSetupState & {
		// Todo: audienceSourceType appears to only be set on the Distribute page. Need a way to fetch it on page load
		audienceSourceType?: AudienceSourceType;
		availableBreakouts: IBreakoutModelVm;
		clientId: number;
		hrisExists: boolean;
		hrisFileName: string;
		hrisId?: string;
		selectedBenchmarkIds: number[];
		surveyId: number;
		userType: UserType;
		userFeatureAccessHelper: IUserFeatureAccessHelper;
	} & ISurveyBuilderActionCreators &
	IBenchmarksActionCreators &
	IBreakoutsActionCreators &
	IReportingSetupActionCreators &
	ISurveyBenchmarkActionCreators;

export class ReportingSetup extends React.PureComponent<IReportingSetupProps> {
	public UNSAFE_componentWillMount() {
		this.props.GetBenchmarksIfNeeded();
		if (this.props.surveyId !== 0) {
			this.props.GetSurveyBenchmarks(this.props.surveyId);
		}
	}

	public componentDidMount() {
		this.props.GetAvailableAttributes(
			this.props.surveyId,
			this.props.reportingSetupModel.audienceId,
			this.props.clientId,
			this.props.reportingSetupModel.distributionType,
		);
	}

	public componentWillUnmount() {
		this.props.UnmountReportingSetup();
	}

	public UNSAFE_componentWillReceiveProps(props: IReportingSetupProps) {
		if (props.surveyId !== 0 && props.surveyId !== this.props.surveyId) {
			this.props.GetSurveyBenchmarks(props.surveyId);
		}
		// We need this for page refresh and times when props aren't set yet in componentDidMount.
		if (
			props.clientId !== 0 &&
			(props.reportingSetupModel.audienceId !== this.props.reportingSetupModel.audienceId ||
				props.clientId !== this.props.clientId ||
				props.audienceSourceType !== this.props.audienceSourceType ||
				props.hrisId !== this.props.hrisId)
		) {
			this.props.GetAvailableAttributes(
				props.surveyId,
				props.reportingSetupModel.audienceId,
				props.clientId,
				props.reportingSetupModel.distributionType,
			);
		}

		if (
			props.availableBreakouts.attributes.length !== this.props.availableBreakouts.attributes.length &&
			props.availableBreakouts.attributes.length > 0
		) {
			this.props.CheckForMissingBreakouts(props.availableBreakouts, props.reportingSetupModel.participationMonitorBreakout);
		}
	}

	public render() {
		const {
			audienceSourceType,
			availableBenchmarks,
			availableBreakouts,
			benchmarksLoading,
			breakoutsLoading,
			firstTrendGroupItem,
			hrisExists,
			hrisFileName,
			hrisId,
			loading,
			missingBreakouts,
			reportingSetupModel,
			selectedBenchmarkIds,
			selectedTrendGroupName,
			surveyId,
			trendGroups,
			trendSurveys,
			trendSurveysLoading,
			userType,
			validationResults,
			userFeatureAccessHelper,
			AddUser,
			DeleteUser,
			UpdateSelectedAttributes,
			UpdateSelectedHierarchies,
			UpdateUser,
		} = this.props;

		const selectedBenchmarks = availableBenchmarks.filter((bm: IBenchmark) => selectedBenchmarkIds.indexOf(bm.benchmarkId) !== -1);

		const showBulkReporting =
			userFeatureAccessHelper.CanUserAccessFeature('EnableReportingBulkUsers') &&
			audienceSourceType === AudienceSourceType.Hris &&
			DistributionHelper.isNotAnonymous(reportingSetupModel.distributionType);

		const showReportingTrendsComponent = !showBulkReporting;

		const trendGroupsValidation = new ValidationCollectionHelper(validationResults).GetValidationResultForProperty('SelectedTrendGroupId');

		return (
			<CardLayout
				loading={loading || benchmarksLoading}
				title={ContentConstants.TITLE_REPORTING_SETUP}
				header={<SurveyNoteHeaderContainer />}
			>
				<ReportingBenchmarks {...this.props} benchmarks={selectedBenchmarks} />
				{showReportingTrendsComponent && (
					<ReportingTrends
						validationResults={trendGroupsValidation}
						firstTrendGroupItem={firstTrendGroupItem}
						trendGroups={trendGroups}
						trendSurveys={trendSurveys}
						trendSurveysLoading={trendSurveysLoading}
						selectedTrendGroupId={reportingSetupModel.selectedTrendGroupId}
						selectedTrendGroupName={selectedTrendGroupName}
						onTrendGroupChange={(trendGroupId) => this.props.ChangeTrendGroup(surveyId, trendGroupId)}
					/>
				)}

				{userFeatureAccessHelper.CanUserAccessFeature('EnableResponseStream') && (
					<SurveyResponseStreamSetup
						ResponseStreamFlagChanged={this.props.ResponseStreamFlagChanged}
						isResponseStreamed={this.props.reportingSetupModel.isResponseStreamed}
					/>
				)}
				<ReportingUsers
					AddUser={() => AddUser(surveyId)}
					DeleteUser={DeleteUser}
					UpdateUser={UpdateUser}
					reportingUsers={reportingSetupModel.reportingUsers}
					surveyId={surveyId}
					userType={userType}
					validationResults={validationResults}
					distributionType={reportingSetupModel.distributionType}
				/>
				<Permission {...PermissionConstants.HIERARCHYREPORTING_ALL}>
					{reportingSetupModel.distributionType !== undefined && (
						<ReportingProfileContainer surveyId={surveyId} distributionType={reportingSetupModel.distributionType} />
					)}
				</Permission>
				{userType === UserType.Internal && (
					<ParticipationMonitorBreakouts
						{...this.props}
						audienceSourceType={audienceSourceType}
						audienceId={reportingSetupModel.audienceId}
						distributionType={reportingSetupModel.distributionType}
						hrisId={hrisId}
						hrisExists={hrisExists}
						hrisFileName={hrisFileName}
						availableBreakouts={availableBreakouts}
						loading={breakoutsLoading}
						selectedBreakouts={reportingSetupModel.participationMonitorBreakout}
						missingBreakouts={missingBreakouts}
						onSelectedAttributesChange={UpdateSelectedAttributes}
						onSelectedHierarchiesChange={UpdateSelectedHierarchies}
					/>
				)}
				<Divider type='dashed' />
				<SaveButtonsContainer>
					<ButtonGroup type='column' reversed={false}>
						<Button id='PreviousStepButton' color='secondary' onClick={() => this.props.PreviousStep()} size='lg'>
							{ContentConstants.BUTTONS_PREVIOUS_STEP}
						</Button>
						<Button id='SaveButton' color='secondary' size='lg' onClick={() => this.props.Save(false)} disabled={loading}>
							{ContentConstants.BUTTONS_SAVE}
						</Button>
						<Button id='SaveAndNextStepButton' color='primary' onClick={() => this.props.Save(true)} size='lg' disabled={this.props.saving}>
							{ContentConstants.BUTTONS_SAVE_AND_NEXT}
						</Button>
					</ButtonGroup>
				</SaveButtonsContainer>
			</CardLayout>
		);
	}
}
